import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS, iconForKey, Mark } from "@/lib/theme";
import { apiGet, apiPost } from "@/lib/api";
import { Tin, Coin } from "@/components/TinCoin";
import BackHomeBar from "@/components/BackHomeBar";

/**
 * Donation flow:
 *  tin -> categories -> amounts -> Payfast hosted checkout
 */
import { isPhoneApp, AppLogo } from "@/lib/phoneApp";

export default function Give({ onDone }) {
  const [step, setStep] = useState("tin"); // tin | categories | amounts | payment | thanks
  const [dropping, setDropping] = useState(false);
  const [categories, setCategories] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet("/donation-categories").then((d) => {
      const available = d.categories || [];
      const phoneCauses = [["chesed", "Chesed"], ["yartzheit", "Yahrzeit"], ["candle_lighting", "Candle Lighting"], ["tehillim", "Tehillim"], ["rabbi_discretionary", "Rabbi Discretionary Fund"], ["daily_service", "Morning Minyan"]];
      setCategories(isPhoneApp ? phoneCauses.flatMap(([id, name]) => available.filter((cause) => cause.id === id).map((cause) => ({ ...cause, name }))) : available);
      setPresets(d.presets || []);
    }).catch(() => {});
  }, []);

  const tapTin = () => {
    setDropping(true);
    setTimeout(() => {
      setDropping(false);
      setStep("categories");
    }, 900);
  };

  const chooseCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedAmount(null);
    setCustomAmount("");
    setStep("amounts");
  };

  const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const proceedToPayment = async () => {
    if (!amount || amount <= 0) return;
    setBusy(true); setError("");
    try {
      const res = await apiPost("/donations/create-payment", {
        category: selectedCategory.id,
        amount,
        is_custom_amount: !!customAmount,
      });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = res.action;
      for (const [name, value] of Object.entries(res.fields)) {
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = name;
        field.value = value;
        form.appendChild(field);
      }
      document.body.appendChild(form);
      form.submit();
      form.remove();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Payment failed to start");
    } finally {
      setBusy(false);
    }
  };

  const backToHome = () => {
    setStep("tin");
    setSelectedCategory(null);
    setSelectedAmount(null);
    setCustomAmount("");
    onDone && onDone();
  };

  return (
    <div className="ls-scroll" data-testid="give-screen" style={{ position: "relative", paddingBottom: 100 }}>
      {step === "tin" && <BackHomeBar title={isPhoneApp ? "Donate" : "Give"} onHome={backToHome} />}
      {/* Step: TIN */}
      {step === "tin" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 22px 0" }}>
          {isPhoneApp ? <AppLogo size={80} /> : <div className="font-heading" style={{ fontWeight: 800, fontSize: 24, color: COLORS.ink }}>Give with a coin</div>}
          <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginTop: 4, textAlign: "center" }}>
            {isPhoneApp ? "Tap the Tzedakah Box and select your preference and amount" : "Tap the tin to choose a cause and amount"}
          </div>
          <div style={{ height: 110, marginTop: 26 }}>
            <Coin animate={dropping} />
          </div>
          <div style={{ marginTop: 14 }} data-testid="give-tin-tap">
            {isPhoneApp ? <button type="button" onClick={tapTin} aria-label="Open Tzedakah Box" style={{ width: 160, padding: 0, overflow: "hidden", border: 0, borderRadius: 10, background: COLORS.plum, color: "#fff" }}>
              <svg width="160" height="58" viewBox="0 0 160 58" aria-hidden="true"><path d="M0 28 30 4h100l30 24v30H0Z" fill={COLORS.gold} /><path d="M59 19h42" stroke={COLORS.plum} strokeWidth="5" strokeLinecap="round" /></svg>
              <span style={{ display: "block", padding: "15px 6px", fontWeight: 800 }}>Tzedakah Box</span>
            </button> : <Tin onTap={tapTin} shake={dropping} />}
          </div>
        </div>
      )}

      {/* Step: CATEGORIES */}
      {step === "categories" && (
        <div className="anim-fadeUp" style={{ padding: "26px 22px 0" }}>
          <button
            onClick={() => setStep("tin")}
            aria-label="Back"
            className="no-tap-highlight"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}
          >
            <Ic d={ICONS.back} color={COLORS.purple} size={28} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            {isPhoneApp ? <AppLogo size={52} /> : <Mark size={52} />}
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink, marginTop: 4 }}>{isPhoneApp ? "Choose a cause you care about" : "Choose a cause"}</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13 }}>Every mitzvah counts</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`give-category-${c.id}`}
                onClick={() => chooseCategory(c)}
                className="ls-card no-tap-highlight"
                style={{ padding: "18px 12px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 12, background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic d={iconForKey(c.icon)} color={COLORS.plum} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 13.5, color: COLORS.ink, lineHeight: 1.2 }}>{c.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step: AMOUNTS */}
      {step === "amounts" && selectedCategory && (
        <div className="anim-fadeUp" style={{ padding: "26px 22px 100px" }}>
          <button onClick={() => setStep("categories")} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}>
            <Ic d={ICONS.back} color={COLORS.purple} size={28} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            {isPhoneApp ? <AppLogo size={48} /> : <Mark size={48} />}
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 18, color: COLORS.purple }}>{selectedCategory.name}</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, marginTop: 2, fontSize: 13 }}>Please select an amount (ZAR)</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {presets.map((a) => {
              const on = selectedAmount === a && !customAmount;
              return (
                <button
                  key={a}
                  data-testid={`give-amount-${a}`}
                  onClick={() => { setSelectedAmount(a); setCustomAmount(""); }}
                  className="no-tap-highlight"
                  style={{
                    padding: "18px 0",
                    borderRadius: 16,
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 21,
                    color: on ? "#fff" : COLORS.purple,
                    background: on ? `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})` : "#fff",
                    border: `2px solid ${on ? COLORS.purple : "#ead9f2"}`,
                    boxShadow: on ? "0 6px 16px rgba(91,42,130,0.3)" : "none",
                    fontFamily: "'Nunito', sans-serif",
                  }}
                >
                  R{a}
                </button>
              );
            })}
          </div>
          <div style={{ height: 1, background: "#e3d8ec", margin: "20px 0 14px" }} />
          <input
            data-testid="give-custom-amount"
            value={customAmount}
            onChange={(e) => { setCustomAmount(e.target.value.replace(/[^0-9.]/g, "")); setSelectedAmount(null); }}
            placeholder="Or enter a custom amount (ZAR)"
            inputMode="decimal"
            className="ls-input"
            style={{ textAlign: "center", fontWeight: 800, fontSize: 17, color: COLORS.purple }}
          />
          {error && <div style={{ color: "#a02040", fontSize: 13, fontWeight: 700, textAlign: "center", marginTop: 10 }}>{error}</div>}
          <button
            data-testid="give-donate-btn"
            className="ls-primary-btn"
            style={{ width: "100%", marginTop: 14, padding: 17, fontSize: 18 }}
            disabled={busy || (!selectedAmount && !customAmount)}
            onClick={proceedToPayment}
          >
            {busy ? "Please wait…" : `Donate R${amount || 0}`}
          </button>
        </div>
      )}


    </div>
  );
}
